import { useState, useEffect, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Select, { MultiValue } from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import ValidationMessage from "src/helpers/ValidationMessage";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import PATHS from "src/routes/Paths";

interface CreateGroupChatProp {
  onShowModal: boolean;
  setShowModal: (visible: boolean) => void;
  setModified: (visible: boolean) => void;
}
type SelectedObject = {
  value: string;
  label: string;
};

const CreateGroupChat: React.FC<CreateGroupChatProp> = ({
  onShowModal,
  setShowModal,
  setModified,
}) => {
  const { t } = useTranslation();
  const navigate=useNavigate()
  //States
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<
    MultiValue<SelectedObject>
  >([]);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<
    MultiValue<SelectedObject>
  >([]);
  const [subject, setSubject] = useState("");
  const [validations, setValidations] = useState<Record<string, string>>({});
  const [btnLoading, setBtnLoading] = useState(false);

 
  const onClose = () => {
    setShowModal(false);
  };

  
  const getRoleList = async () => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.getRoles,
        "GET",
        API_HEADERS.authenticated
      );
      // console.log(response)
      setRoles(response.roles.data);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
    }
  };

 
  const getUsers = async (roleIds: string[]) => {
    if (!roleIds || roleIds.length === 0) {
      setUsers([]); 
      return;
    }

    const roleIdsQuery = roleIds
      .map((roleId) => `role_ids[]=${roleId}`)
      .join("&");
    const requestUrl = `${API_PATHS.threadRecipients}?${roleIdsQuery}`;

    try {
      const response: any = await makeApiCall<ResponseType>(
        requestUrl,
        "GET",
        API_HEADERS.authenticated
      );

      const newUsers = response.recipients;

    
      setUsers(newUsers);
    } catch (error: any) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
    }
  };

 
  useEffect(() => {
    getRoleList();
  }, []);


  useEffect(() => {
    const roleIds = selectedRoles.map((role) => role.value); 
    getUsers(roleIds); 
  }, [selectedRoles]);
  

 const handleRoleChange = (selected: MultiValue<SelectedObject>) => {
   setSelectedRoles(selected);

   // Fetch users based on the selected roles
   const roleIds = selected.map((role) => role.value);
   getUsers(roleIds);
 };

  const handleUserChange = (selected: MultiValue<SelectedObject>) => {
    setSelectedUsers(selected); 
  };

  
  const createChat = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setBtnLoading(true);

    const formData = {
      subject: subject,
      recipients: selectedUsers.map((user) => user.value),
    };
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.createNewChat,
        "POST",
        API_HEADERS.authenticated,
        formData
      );
      FLASH_MESSAGES.successMsg(response.success);
      setModified(true);
      onClose();
       const threadId = response.thread_id;
       navigate(PATHS.messagesOfThreads(threadId));
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Modal isOpen={onShowModal} toggle={() => onClose()} size="md" centered>
      <ModalHeader toggle={() => onClose()}>
        {t("chat_group_title_popup")}
      </ModalHeader>
      <form onSubmit={createChat}>
        <ModalBody>
          <p className="popup_subtitle mb-4">{t("add_group_description")}</p>
          <div className="row">
            <div className="form-block col-12 col-md-6">
              <label className="required">
                {t("create_new_conversation_role")}
              </label>
              <Select
                placeholder={t("create_new_conversation_choose_role")}
                className="form-react-select"
                classNamePrefix="select_input"
                name="role"
                onChange={handleRoleChange}
                isClearable={true}
                isSearchable={false}
                isMulti
                options={roles.map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                value={selectedRoles}
              />
            </div>
            <div className="form-block col-12 col-md-6">
              <label className="required">
                {t("create_new_conversation_name")}
              </label>
              <Select
                name="name"
                className={
                  "form-react-select" +
                  (validations.recipients ? " input-error" : "")
                }
                classNamePrefix="select_input"
                onChange={handleUserChange}
                value={selectedUsers}
                placeholder={t("create_new_conversation_enter_name")}
                options={users.map((item) => ({
                  value: item.id,
                  label: `${item.first_name} ${item.last_name}`,
                }))}
                isMulti
              />
              {validations.recipients && (
                <ValidationMessage message={validations.recipients[0]} />
              )}
            </div>
            <div className="form-block col-12 col-md-6">
              <label>{t("create_new_conversation_subject")}</label>
              <input
                type="text"
                name="subject"
                className={
                  "form-input" + (validations.subject ? " input-error" : "")
                }
                onChange={(e) => setSubject(e.target.value)}
                value={subject}
                placeholder={t("create_new_conversation_enter_subject")}
              />
              {validations.subject && (
                <ValidationMessage message={validations.subject[0]} />
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="button-white"
            type="button"
            onClick={() => onClose()}
          >
            {t("create_new_conversation_close")}
          </button>
          {btnLoading ? (
            <button type="button" className="button-blue has-icon">
              <i className="icon spinner-icon" />
              {t("create_new_conversation_sending")}
            </button>
          ) : (
            <button type="submit" className="button-blue has-icon">
              <i className="icon plus-icon" />
              {t("create_new_conversation_send")}
            </button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CreateGroupChat;
